import { useEffect, useRef } from 'react';
import { useFocusTrap } from '../../utils/accessibility';

export default function ConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'danger', // 'danger' | 'primary'
  icon = null // Optional custom icon
}) {
  const modalRef = useRef(null);
  const cancelButtonRef = useRef(null);

  // Focus trap for accessibility
  useFocusTrap(isOpen, modalRef);

  useEffect(() => {
    if (isOpen) {
      // Focus the cancel button by default for safety
      cancelButtonRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      }
    }
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const confirmButtonClass = confirmVariant === 'danger' 
    ? 'btn-danger' 
    : 'btn-primary';

  return (
    <div 
      className="fixed inset-0 z-[60] overflow-y-auto"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-message"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          ref={modalRef}
          className="relative rounded-lg shadow-2xl w-full max-w-sm animate-scale-in"
          style={{ backgroundColor: 'var(--color-bg-canvas)' }}
        >
          <div className="p-6">
            {/* Icon */}
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ 
                backgroundColor: confirmVariant === 'danger' 
                  ? 'rgba(193, 96, 92, 0.15)' 
                  : 'rgba(189, 166, 110, 0.15)',
              }}
              aria-hidden="true"
            >
              {icon || <WarningIcon style={{ color: 'var(--color-status-error)' }} />}
            </div>
            
            {/* Content */}
            <h3 
              id="confirm-modal-title"
              className="text-lg font-semibold text-center mb-2" 
              style={{ color: 'var(--color-text-primary)' }}
            >
              {title}
            </h3>
            <p 
              id="confirm-modal-message"
              className="text-sm text-center" 
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {message}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-3 px-6 pb-6">
            <button
              ref={cancelButtonRef}
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`${confirmButtonClass} flex-1`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WarningIcon({ style }) {
  return (
    <svg className="w-6 h-6" style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}
